import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} QuizAnswer
 * @property {number} id
 * @property {string} selected
 */

/**
 * @typedef {object} StoreQuizProgressPayload
 * @property {number} quest_chapter_id
 * @property {QuizAnswer[]} answers
 * @property {number} [time_spent_seconds]
 * @property {number} [passing_threshold]
 */

/**
 * Hook to store Student Quiz Progress.
 *
 * @returns {{
 *  storeQuizProgress: (payload: StoreQuizProgressPayload) => Promise<object|null>,
 *  loading: boolean,
 *  error: string,
 *  success: boolean
 * }}
 */
export const useStoreQuizProgress = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const { axiosInstance } = useAxios();

  const storeQuizProgress = async (payload) => {
    try {
      setLoading(true);
      setError("");
      setSuccess(false);

      const body = {
        quest_chapter_id: payload.quest_chapter_id,
        answers: payload.answers,
      };

      // Optional fields
      if (payload.time_spent_seconds !== undefined) {
        body.time_spent_seconds = payload.time_spent_seconds;
      }

      if (payload.passing_threshold !== undefined) {
        body.passing_threshold = payload.passing_threshold;
      }

      const response = await axiosInstance.post(
        `${TENANT_DOMAIN}/student/progress/quiz`,
        body
      );

      setSuccess(true);
      return response?.data;
    } catch (err) {
      setSuccess(false);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    storeQuizProgress,
    loading,
    error,
    success,
  };
};
